import { Location } from '@angular/common';
import { DOCUMENT, inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import {
  CheckoutApiService,
  CheckoutStore,
  RELEASE_SEATS_FAILED_MESSAGE,
} from '@seatix/checkout-data-access';
import {
  CheckoutDialogService,
  CheckoutExitConfirmationComponent,
} from '@seatix/checkout-ui';
import { AlertService } from '@seatix/shared-util-alert';
import { LoadingService } from '@seatix/shared-util-loading';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

export const checkoutExitPreventionGuard: CanDeactivateFn<unknown> = (
  _component,
  _currentRoute,
  currentState,
  nextState,
) => {
  const store = inject(CheckoutStore);
  const dialog = inject(CheckoutDialogService);
  const location = inject(Location);
  const checkoutApi = inject(CheckoutApiService);
  const loadingService = inject(LoadingService);
  const alert = inject(AlertService);
  const _window = inject(DOCUMENT).defaultView;

  const order = store.order();
  const orderToken = store.orderToken();
  const isExpired = store.isExpired();

  // Allow navigation if:
  // - No active order/token
  // - Order is expired
  // - Order is already completed
  // - Navigating to success page of same order
  if (
    !order ||
    !orderToken ||
    isExpired ||
    store.isOrderCompleted(orderToken) ||
    nextState?.url?.includes(`/${orderToken}/success`)
  ) {
    return true;
  }

  // Fix URL immediately when blocking navigation
  // This prevents the address bar from showing the wrong URL
  location.go(currentState.url);

  return showExitConfirmationDialog();

  function showExitConfirmationDialog(): Observable<boolean> {
    const { ref, reused } = dialog.openExclusive<
      CheckoutExitConfirmationComponent,
      unknown,
      boolean
    >(CheckoutExitConfirmationComponent, {
      disableClose: true,
      width: '400px',
    });

    // The page opened this dialog first and is still subscribed to it: its
    // own subscriber releases the seats. `afterClosed()` is multicast, so
    // releasing here too would fire a second `POST /release` against an
    // already-cancelled order. The page also owns the exit: it leaves only
    // once the release succeeds, so the router never leaves ahead of it.
    if (reused) {
      return ref.afterClosed().pipe(map(() => false));
    }

    return ref.afterClosed().pipe(
      switchMap((result) => {
        const shouldExit = result === true;

        if (shouldExit && order?.id) {
          const loadingRef = loadingService.show({
            message: 'Liberando asientos...',
            cancelable: false,
          });

          return checkoutApi.releaseSeats(order.id).pipe(
            tap(() => {
              loadingRef.updateMessage('Asientos liberados exitosamente');
              store.clearOrder();
              _window?.history.replaceState({}, '', '/events');
            }),
            map(() => true),
            // Whatever the status, a failed release means the seats may still
            // be held — or the order already moved on. Keep the buyer here and
            // let the order's real status decide what they see.
            catchError(() => {
              alert.showError(RELEASE_SEATS_FAILED_MESSAGE);
              store.reloadOrder();
              return of(false);
            }),
            finalize(() => loadingRef.close()),
          );
        } else {
          store.backToSelection();
        }

        return of(false);
      }),
    );
  }
};
